import { Logo } from "@/components/Logo"
import { PayoutReceipt } from "@/components/PayoutReceipt"
import { AppCard } from "@/components/ui/AppCard"
import { useTRPC } from "@/trpc/client"
import { useQuery } from "@tanstack/react-query"
import {
  Box,
  Center,
  HStack,
  Image,
  Separator,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { CheckCircle2, Circle, Loader2, XCircle } from "lucide-react"
import { useParams } from "react-router-dom"

const StatusStep = ({
  label,
  sublabel,
  status,
  isLast = false,
}: {
  label: string
  sublabel: string
  status: "completed" | "current" | "pending" | "error"
  isLast?: boolean
}) => {
  const getIcon = () => {
    switch (status) {
      case "completed":
        return <CheckCircle2 size={20} color="var(--chakra-colors-green-500)" />
      case "current":
        return (
          <Loader2
            size={20}
            color="var(--chakra-colors-blue-500)"
            className="animate-spin"
          />
        )
      case "error":
        return <XCircle size={20} color="var(--chakra-colors-red-500)" />
      default:
        return <Circle size={20} color="var(--chakra-colors-border-subtle)" />
    }
  }

  return (
    <HStack align="flex-start" gap="4" position="relative" width="full">
      <VStack gap="0" align="center" position="relative">
        <Box zIndex="1" bg="bg.panel" borderRadius="full">
          {getIcon()}
        </Box>
        {!isLast && (
          <Box
            width="2px"
            height="10"
            bg={status === "completed" ? "green.500" : "border.subtle"}
            mt="1"
          />
        )}
      </VStack>
      <VStack align="start" gap="0" pt="0.5">
        <Text
          fontSize="sm"
          fontWeight="bold"
          color={status === "pending" ? "fg.muted" : "fg.default"}
        >
          {label}
        </Text>
        <Text fontSize="xs" color="fg.subtle">
          {sublabel}
        </Text>
      </VStack>
    </HStack>
  )
}

export const PayoutTracker = () => {
  const { merchantId, id } = useParams<{ merchantId: string; id: string }>()
  const trpc = useTRPC()

  const {
    data: payout,
    isPending,
    isError,
  } = useQuery({
    ...trpc.payouts.getPublic.queryOptions({
      merchantId: merchantId || "",
      id: id || "",
    }),
    enabled: !!id && !!merchantId,
  })

  if (isPending) {
    return (
      <Center minH="100vh" bg="bg.muted">
        <Spinner size="xl" color="brand.fg" />
      </Center>
    )
  }

  if (isError || !payout) {
    return (
      <Center minH="100vh" bg="bg.muted" px="4">
        <AppCard maxW="400px" p="10" textAlign="center">
          <Text fontWeight="bold" mb="2">
            Pago no encontrado
          </Text>
          <Text color="fg.muted" fontSize="sm">
            No pudimos encontrar la información de este pago. Por favor,
            verifica el enlace o contacta a soporte.
          </Text>
        </AppCard>
      </Center>
    )
  }

  const getStepStatus = (
    step: number
  ): "completed" | "current" | "pending" | "error" => {
    // Step 1: "En proceso"
    // Step 2: Final result

    if (payout.status === "completed") {
      return step === 1 ? "completed" : "completed"
    }

    if (payout.status === "declined") {
      // We attempted processing but it was rejected
      return step === 1 ? "completed" : "error"
    }

    if (payout.status === "cancelled") {
      // Cancelled - no processing really happened
      return step === 1 ? "error" : "error"
    }

    if (payout.status === "processing") {
      return step === 1 ? "current" : "pending"
    }

    if (payout.status === "pending") {
      return step === 1 ? "current" : "pending"
    }

    return "pending"
  }

  const getStep2Label = () => {
    switch (payout.status) {
      case "completed":
        return {
          label: "Completado",
          sublabel: "El dinero ya está en tu cuenta",
        }
      case "declined":
        return {
          label: "Rechazado",
          sublabel: "El pago fue rechazado por el banco",
        }
      case "cancelled":
        return {
          label: "Cancelado",
          sublabel: "El pago fue cancelado por la inmobiliaria",
        }
      default:
        return {
          label: "Completado",
          sublabel: "El dinero ya debería estar en tu cuenta",
        }
    }
  }

  const step2 = getStep2Label()

  return (
    <Box
      minH="100vh"
      bg="bg.muted"
      py={{ base: "8", md: "16" }}
      px="4"
      position="relative"
    >
      <Center>
        <VStack gap="8" width="full" maxW="450px">
          {/* Centered Merchant Logo */}
          <Box mb="2">
            {payout.merchantLogoUrl ? (
              <Image
                src={payout.merchantLogoUrl}
                alt={payout.merchantName}
                maxHeight="64px"
                maxWidth="240px"
                objectFit="contain"
              />
            ) : (
              <Text fontSize="2xl" fontWeight="800" color="fg.default">
                {payout.merchantName}
              </Text>
            )}
          </Box>

          {/* Progress Tracker Card */}
          <AppCard p="4" width="full">
            <VStack align="start" gap="1" mb="6">
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="brand.fg"
                letterSpacing="widest"
                textTransform="uppercase"
              >
                Seguimiento de Pago
              </Text>
            </VStack>

            <VStack align="stretch" gap="0">
              <StatusStep
                label="En proceso"
                sublabel="Estamos enviando el dinero a tu cuenta bancaria"
                status={getStepStatus(1)}
              />
              <StatusStep
                label={step2.label}
                sublabel={step2.sublabel}
                status={getStepStatus(2)}
                isLast
              />
            </VStack>
          </AppCard>

          {/* Receipt Details */}
          <VStack width="full" gap="4">
            <AppCard p="0" overflow="hidden" width="full">
              <PayoutReceipt payout={payout} />
            </AppCard>

            <HStack justify="center" pt="6" opacity="0.6">
              <Logo showText={true} />
              <Separator orientation="vertical" height="3" />
              <Text fontSize="2xs" color="fg.subtle" fontWeight="medium">
                Pagos seguros por Palomma
              </Text>
            </HStack>
          </VStack>
        </VStack>
      </Center>
    </Box>
  )
}
